import { useEffect, useRef, useState } from "react"
import { AudioLines, Film, Plus, Video, VideoOff } from "lucide-react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import type { AnimaticMode } from "@/lib/timeline"
import type { TimelineAudioAsset, TimelineVersionOption } from "./types"

type Panel = "root" | "version" | "audio" | "animatic"

type Props = {
  versions: TimelineVersionOption[]
  audioAssets: TimelineAudioAsset[]
  canAddVideo: boolean
  canAddAnimatic: boolean
  canAddAnimaticVideo: boolean
  onAddVideo: (versionId: number) => void
  onAddAudio: () => void
  onAddAudioAsset: (assetId: number, durationMs: number | null) => void
  onAddAnimatic: (mode: AnimaticMode) => void
}

const itemClass =
  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-ui-text-primary transition-colors hover:bg-ui-surface-raised disabled:cursor-not-allowed disabled:opacity-40"

const mediaRowClass =
  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-ui-surface-raised"

/** «+» в тулбаре: видеодорожка (версия шота), аниматик или кастомная аудиодорожка. */
export function AddTrackMenu({
  versions,
  audioAssets,
  canAddVideo,
  canAddAnimatic,
  canAddAnimaticVideo,
  onAddVideo,
  onAddAudio,
  onAddAudioAsset,
  onAddAnimatic,
}: Props) {
  const { t } = useTranslation(["shot"])
  const [open, setOpen] = useState(false)
  const [panel, setPanel] = useState<Panel>("root")
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setPanel("root")
      }
    }
    document.addEventListener("pointerdown", onDown)
    return () => document.removeEventListener("pointerdown", onDown)
  }, [open])

  const close = () => {
    setOpen(false)
    setPanel("root")
  }

  const pickAnimatic = (mode: AnimaticMode) => {
    onAddAnimatic(mode)
    close()
  }

  const readyAudio = audioAssets.filter((asset) => asset.status === "ready").reverse()

  return (
    <div ref={rootRef} className="relative inline-flex leading-none">
      <ActionHintTooltip label={t("timeline.addTrack")}>
        <button
          type="button"
          onClick={() => {
            setOpen((v) => !v)
            setPanel("root")
          }}
          className={cn(
            "rounded p-1.5 transition-colors",
            open
              ? "bg-ui-surface-raised text-ui-accent-text"
              : "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
          )}
        >
          <Plus className="h-4 w-4" />
        </button>
      </ActionHintTooltip>
      {open ? (
        <div className="absolute right-0 bottom-full z-40 mb-1 w-56 rounded-lg border border-ui-border p-1 shadow-xl dark:bg-zinc-900 light:bg-ui-bg-elevated">
          {panel === "root" ? (
            <>
              <button type="button" disabled={!canAddVideo} onClick={() => setPanel("version")} className={itemClass}>
                <Video className="h-3.5 w-3.5 shrink-0" />
                {t("timeline.addVideoTrack")}
              </button>
              <button type="button" onClick={() => setPanel("audio")} className={itemClass}>
                <AudioLines className="h-3.5 w-3.5 shrink-0" />
                {t("timeline.addAudioTrack")}
              </button>
              <button
                type="button"
                disabled={!canAddAnimatic}
                title={!canAddAnimatic ? t("timeline.animaticUnavailable") : undefined}
                onClick={() => setPanel("animatic")}
                className={itemClass}
              >
                <Film className="h-3.5 w-3.5 shrink-0" />
                {t("timeline.addAnimaticTrack")}
              </button>
            </>
          ) : null}
          {panel === "version" ? (
            <div className="it2tv-scrollbar max-h-56 overflow-y-auto">
              <p className="px-2 py-1 text-[10px] text-ui-text-muted">{t("timeline.pickVersion")}</p>
              {versions.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    onAddVideo(v.id)
                    close()
                  }}
                  className={mediaRowClass}
                >
                  {v.preview_thumb_url ? (
                    <img src={v.preview_thumb_url} alt="" className="h-7 w-10 shrink-0 rounded object-cover" />
                  ) : (
                    <div className="flex h-7 w-10 shrink-0 items-center justify-center rounded bg-ui-surface-sunken">
                      <Video className="h-3 w-3 text-ui-text-muted" />
                    </div>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11px] text-ui-text-primary">
                      {v.name}
                      {v.active ? (
                        <span className="ml-1 text-[9px] text-ui-accent-text">{t("timeline.activeVersion")}</span>
                      ) : null}
                    </span>
                    <span className="block truncate text-[9px] text-ui-text-muted tabular-nums">
                      {v.duration_ms != null ? `${(v.duration_ms / 1000).toFixed(1)}s · ` : ""}
                      {new Date(v.created_at).toLocaleString()}
                      {v.generation_model ? ` · ${v.generation_model}` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {panel === "audio" ? (
            <div className="it2tv-scrollbar max-h-56 overflow-y-auto">
              <p className="px-2 py-1 text-[10px] text-ui-text-muted">{t("timeline.pickAudioTrack")}</p>
              <button
                type="button"
                onClick={() => {
                  onAddAudio()
                  close()
                }}
                className={itemClass}
              >
                <AudioLines className="h-3.5 w-3.5 shrink-0" />
                {t("timeline.emptyAudioTrack")}
              </button>
              {readyAudio.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => {
                    onAddAudioAsset(asset.id, asset.duration_ms)
                    close()
                  }}
                  className={mediaRowClass}
                >
                  <div className="flex h-7 w-10 shrink-0 items-center justify-center rounded bg-ui-surface-sunken">
                    <AudioLines className="h-3 w-3 text-ui-text-muted" />
                  </div>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11px] text-ui-text-primary">
                      {asset.name?.trim() || t("timeline.audioAssetFallback")}
                    </span>
                    {asset.duration_ms != null ? (
                      <span className="block truncate text-[9px] text-ui-text-muted tabular-nums">
                        {(asset.duration_ms / 1000).toFixed(1)}s
                      </span>
                    ) : null}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {panel === "animatic" ? (
            <div>
              <p className="px-2 py-1 text-[10px] text-ui-text-muted">{t("timeline.pickAnimatic")}</p>
              <button
                type="button"
                disabled={!canAddAnimaticVideo}
                onClick={() => pickAnimatic("video_with_audio")}
                className={itemClass}
              >
                <Video className="h-3.5 w-3.5 shrink-0" />
                {t("timeline.animaticVideoWithAudio")}
              </button>
              <button
                type="button"
                disabled={!canAddAnimaticVideo}
                onClick={() => pickAnimatic("video_silent")}
                className={itemClass}
              >
                <VideoOff className="h-3.5 w-3.5 shrink-0" />
                {t("timeline.animaticVideoOnly")}
              </button>
              <button type="button" onClick={() => pickAnimatic("audio_only")} className={itemClass}>
                <AudioLines className="h-3.5 w-3.5 shrink-0" />
                {t("timeline.animaticAudioOnly")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
